import React, { useMemo, useState } from 'react';
import I18n from '@utils/i18n';
import { AssigneeType } from '@enums/assignee-type.enum';
import { Button, DateInput, Modal, ModalBody, ModalFooter, ModalHeader, Radio, SelectInput, TextInput } from '@ui';
import { VisitPriority } from './types';
const pad = (n) => String(n).padStart(2, '0');
export function formatDate(date) {
    return `${pad(date.getMonth() + 1)}/${pad(date.getDate())}/${date.getFullYear()}`;
}
export function parseDate(value) {
    const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value);
    if (!match)
        return null;
    return new Date(Number(match[3]), Number(match[1]) - 1, Number(match[2]));
}
export function formatTime(date) {
    return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}
export function parseTime(value) {
    const match = /^(\d{2}):(\d{2})$/.exec(value);
    if (!match)
        return null;
    const date = new Date();
    date.setHours(Number(match[1]), Number(match[2]), 0, 0);
    return date;
}
function AdditionalVisitModal({ isOpen, initialRow, technicianOptions, subcontractorOptions, subcontractorsEnabled, priorityOptions, onSave, onClose }) {
    var _a, _b;
    const [draft, setDraft] = useState(initialRow);
    const [errors, setErrors] = useState({});
    const update = (patch) => setDraft((prev) => (Object.assign(Object.assign({}, prev), patch)));
    const isSubcontractor = subcontractorsEnabled && draft.assigneeType === AssigneeType.SUBCONTRACTOR;
    const assigneeOptions = isSubcontractor ? subcontractorOptions : technicianOptions;
    const assigneeValue = isSubcontractor ? draft.subcontractorId : draft.technicianId;
    const assigneeTypeOptions = useMemo(() => [
        { value: AssigneeType.TECHNICIAN, label: I18n.t('inspections.form.additional_visits.technician') },
        { value: AssigneeType.SUBCONTRACTOR, label: I18n.t('inspections.form.additional_visits.subcontractor') }
    ], []);
    const handleSave = () => {
        const next = {};
        const required = I18n.t('inspections.form.additional_visits.required');
        if (assigneeValue === null || assigneeValue === undefined)
            next.assignee = required;
        if (!parseDate(draft.scheduledDate))
            next.scheduledDate = required;
        if (!parseTime(draft.scheduledTime))
            next.scheduledTime = required;
        if (draft.duration !== null && draft.duration < 0)
            next.duration = I18n.t('inspections.form.additional_visits.invalid_duration');
        setErrors(next);
        if (Object.keys(next).length > 0)
            return;
        onSave(Object.assign(Object.assign({}, draft), { errors: undefined }));
    };
    const handleAssigneeChange = (value) => {
        if (isSubcontractor) {
            update({ subcontractorId: value, technicianId: null });
        }
        else {
            update({ technicianId: value, subcontractorId: null });
        }
    };
    const handleAssigneeTypeChange = (value) => update({ assigneeType: value, technicianId: null, subcontractorId: null });
    return (React.createElement(Modal, { isOpen: isOpen, onClose: onClose, ariaLabel: I18n.t('inspections.form.additional_visits.modal_title') },
        React.createElement(ModalHeader, { title: I18n.t('inspections.form.additional_visits.modal_title'), onClose: onClose }),
        React.createElement(ModalBody, null,
            subcontractorsEnabled && (React.createElement(Radio, { name: "additional-visit-assignee-type", label: I18n.t('inspections.form.additional_visits.assignee_type'), options: assigneeTypeOptions, value: draft.assigneeType, onChange: handleAssigneeTypeChange })),
            React.createElement(SelectInput, { id: "additional-visit-assignee", label: isSubcontractor
                    ? I18n.t('inspections.form.additional_visits.subcontractor')
                    : I18n.t('inspections.form.additional_visits.technician'), required: true, options: assigneeOptions, value: assigneeValue !== null && assigneeValue !== void 0 ? assigneeValue : undefined, onChange: handleAssigneeChange, error: errors.assignee }),
            React.createElement(DateInput, { id: "additional-visit-date", label: I18n.t('inspections.form.additional_visits.scheduled_date'), required: true, format: "MM/dd/yyyy", value: parseDate(draft.scheduledDate), onChange: (date) => update({ scheduledDate: date ? formatDate(date) : '' }), error: errors.scheduledDate }),
            React.createElement(DateInput, { type: "time", id: "additional-visit-time", label: I18n.t('inspections.form.additional_visits.scheduled_time'), required: true, value: parseTime(draft.scheduledTime), onChange: (time) => update({ scheduledTime: time ? formatTime(time) : '' }), error: errors.scheduledTime }),
            React.createElement(TextInput, { id: "additional-visit-duration", type: "number", min: 0, label: I18n.t('inspections.form.additional_visits.duration'), value: (_a = draft.duration) !== null && _a !== void 0 ? _a : '', onChange: (e) => update({ duration: e.target.value === '' ? null : Number(e.target.value) }), error: errors.duration }),
            React.createElement(SelectInput, { id: "additional-visit-priority", label: I18n.t('inspections.form.additional_visits.priority'), options: priorityOptions, value: (_b = draft.priority) !== null && _b !== void 0 ? _b : VisitPriority.NORMAL, onChange: (value) => update({ priority: value }) }),
            React.createElement(TextInput, { id: "additional-visit-priority-note", multiline: true, label: I18n.t('inspections.form.additional_visits.priority_note'), value: draft.priorityNote, onChange: (e) => update({ priorityNote: e.target.value }) }),
            React.createElement(TextInput, { id: "additional-visit-notes", multiline: true, label: I18n.t('inspections.form.additional_visits.visit_notes'), value: draft.visitNotes, onChange: (e) => update({ visitNotes: e.target.value }) })),
        React.createElement(ModalFooter, null,
            React.createElement(Button, { onClick: onClose }, I18n.t('generic.cancel')),
            React.createElement(Button, { variant: "primary", onClick: handleSave }, I18n.t('inspections.form.additional_visits.save')))));
}
export default AdditionalVisitModal;
